import { Card, Space, Table, Typography } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { ReportComponentResult } from "../../repository/data.default";
import { renderVsTarget } from "../../code/reports/vsTarget";
import BandTag from "../../controls/BandTag";

export interface ComponentBreakdownTableProps {
  readonly components: ReportComponentResult[];
  readonly onDrillKpi: (row: ReportComponentResult) => void;
  /** The report's unit: "%" for scaled KPIs, a count unit for data-collection KPIs, absent when mixed. */
  readonly unit?: string | null;
}

// The value column is named after the report's unit — a count is never labelled a percentage.
const valueTitle = (unit?: string | null) =>
  unit === "%" ? "Value %" : unit ? `Value (${unit})` : "Value";

// Level-1 (non-Gap composites): weighted component breakdown with vs-Target, an expandable
// interpretation / recommended-action row, and clickable KPI rows that drill to respondents.
function ComponentBreakdownTable(props: ComponentBreakdownTableProps) {
  return (
    <Card size="small" title="Component breakdown">
      <Table<ReportComponentResult>
        size="small"
        rowKey={(_r, i) => String(i)}
        pagination={false}
        dataSource={props.components}
        locale={{ emptyText: "No components / data" }}
        expandable={{
          rowExpandable: (r) =>
            !!(r.resolvedInterpretation || r.recommendedAction),
          expandedRowRender: (r) => (
            <Space direction="vertical" size={2}>
              {r.resolvedInterpretation && (
                <Typography.Text>
                  <Typography.Text type="secondary">
                    Interpretation:{" "}
                  </Typography.Text>
                  {r.resolvedInterpretation}
                </Typography.Text>
              )}
              {r.recommendedAction && (
                <Typography.Text>
                  <Typography.Text type="secondary">
                    Recommended action:{" "}
                  </Typography.Text>
                  {r.recommendedAction}
                </Typography.Text>
              )}
            </Space>
          ),
        }}
        onRow={(r) => ({
          onClick: () => props.onDrillKpi(r),
          style:
            r.inputType === "Kpi" && r.inputUid
              ? { cursor: "pointer" }
              : undefined,
        })}
        columns={[
          { title: "Input", dataIndex: "label", key: "label" },
          {
            title: "Type",
            dataIndex: "inputType",
            key: "inputType",
            width: 90,
          },
          {
            title: "Weight",
            dataIndex: "weight",
            key: "weight",
            width: 80,
          },
          {
            title: valueTitle(props.unit),
            dataIndex: "value",
            key: "value",
            width: 90,
            render: (v: number | null) => (v != null ? v.toFixed(1) : "—"),
          },
          {
            title: "vs Target",
            key: "vsTarget",
            width: 150,
            render: (_: unknown, r: ReportComponentResult) => renderVsTarget(r),
          },
          {
            title: "Band",
            dataIndex: "bandLabel",
            key: "bandLabel",
            width: 180,
            render: (label: string | null, r: ReportComponentResult) => (
              <BandTag label={label} color={r.bandColor} />
            ),
          },
          {
            title: "Contribution",
            dataIndex: "contribution",
            key: "contribution",
            width: 110,
            render: (v: number | null) => (v != null ? v.toFixed(1) : "—"),
          },
          {
            title: "",
            key: "drill",
            width: 32,
            align: "center" as const,
            render: (_: unknown, r: ReportComponentResult) =>
              r.inputType === "Kpi" && r.inputUid ? (
                <RightOutlined className="text-slate-400" />
              ) : null,
          },
        ]}
      />
    </Card>
  );
}

export default ComponentBreakdownTable;
